import { Injectable } from '@angular/core';
import {
    EntityCollectionServiceBase,
    EntityCollectionServiceElementsFactory,
} from 'ngrx-data';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { LoginService } from 'src/app/login/services/login-service.service';

import { Document, Alert } from './document.dto';
import { I1FilenetServices } from '../i1-filenet-services.service';
import { mapProperties, mapPropertiesObj } from '../general/generic.function';

@Injectable({
    providedIn: 'root',
})
export class DocumentService extends EntityCollectionServiceBase<Document> {
    failed$ = new Subject<any>();
    constructor(
        serviceElementsFactory: EntityCollectionServiceElementsFactory,
        private loginServce: LoginService,
        private i1FilenetServices: I1FilenetServices
    ) {
        super('Document', serviceElementsFactory);
        // console.log('Document Service Constructed');

        loginServce.loggedOut$.subscribe(v => {
            console.log('User logged out, clearing cache');
            this.clearCache();
        });
    }

    updateAlert(objectStore: string, docID: string, alert: Alert) {
        if (!!alert && !!alert.id && alert.id.startsWith('TEMP')) {
            delete alert.id; // Remove temp id
        }
        const updateAlertObj = mapPropertiesObj({ ...alert });
        return this.i1FilenetServices
            .updateAlert(objectStore, updateAlertObj)
            .pipe(
                switchMap(v =>
                    this.getByID(objectStore, docID, true, true).pipe(
                        map(_ => mapProperties(v))
                    )
                )
            );
    }

    addComment(objectStore: string, docID: string, comment: string) {
        return this.i1FilenetServices
            .addComment(objectStore, docID, comment)
            .pipe(
                switchMap(v =>
                    this.getByID(objectStore, docID, true, true).pipe(
                        map(_ => mapProperties(v))
                    )
                )
            );
    }

    update(document: Document, options: any): Observable<Document> {
        return super.update(document, options).pipe(
            tap(v => console.log('LOG', v)),
            tap(v => {
                if (v === undefined || v === null) {
                    this.failed$.next('Unable to save');
                }
            }),
            tap(v => console.log('Here')),
            filter(v => !!v)
        );
    }

    add(document: Document, options: any): Observable<Document> {
        return super.add(document, options).pipe(
            tap(v => {
                if (v === undefined || v === null) {
                    this.failed$.next('Unable to save');
                }
            }),
            filter(v => !!v)
        );
    }

    bulkUpdate(input: Document[], objectStore: string): Observable<any> {
        console.log('Bulk Updating Documents');
        var updateDocs:Document[] = [];
        for (const document of input){
            const updateDoc:Document = mapPropertiesObj({ ...document }) as Document;
            updateDoc.alerts = updateDoc.alerts
                ? updateDoc.alerts.map(v =>
                    mapPropertiesObj({
                        ...v,
                    })
                )
                : [];
            updateDoc.annotations = updateDoc.annotations
                ? updateDoc.annotations.map(v => mapPropertiesObj({ ...v }))
                : [];

            delete updateDoc.audits; // We dont ever update audits


            delete updateDoc.objectStore; // Not accepted by backend

            updateDocs = updateDocs.concat(updateDoc);
        }
        
        return this.i1FilenetServices.bulkUpdateDocuments(objectStore, updateDocs)
    }

    getDuplicateDocuments(
        objectStoreValue: string,
        titleValue: string,
        hashValue: string,
        sizeValue: string,
        pathValue: string
    ): Observable<Document[]> {
        return this.getWithQuery({
            objectStore: objectStoreValue,
            title: titleValue,
            hash: hashValue,
            size: sizeValue,
            path: pathValue,
            duplicateCheck: 'true',
        });
    }

    displayByIds(DocumentIds: string[]): Observable<Document[]> {
        return this.entities$.pipe(
            map(v => v.filter(ent => DocumentIds.indexOf(ent.id) >= 0))
        );
    }

    displayById(DocumentId: string): Observable<Document> {
        return this.entities$.pipe(
            map(v => v.find(ent => ent.id === DocumentId))
        );
    }

    getByIds(
        objectStoreValue: string,
        DocumentIds: string[],
        completeValue = false,
        searchResultsValue = false
    ): Observable<Document[]> {
        // console.log("T", DocumentIds, completeValue);
        return of(DocumentIds).pipe(
            filter(v => !!v),
            withLatestFrom(this.keys$, this.entities$),
            map(([ids, keys, entities]) => {
                const strKeys = keys as string[];
                return ids.filter(
                    id =>
                        strKeys.indexOf(id) < 0 || // LOGIC TO ONLY INCLUDE KEYS NOT LOADED OR NOT COMPETE IF ASKED
                        (completeValue &&
                            !!entities.find(v => v.id === id) &&
                            !(
                                entities.find(v => v.id === id).complete ||
                                entities.find(v => v.id === id).noAccess
                            ))
                );
            }),
            // tap(v => console.log('IDS', v)),
            switchMap(ids => {
                if (ids === undefined || ids === null || ids.length === 0) {
                    return this.displayByIds(DocumentIds);
                } else {
                    this.setLoading(true);
                    return super
                        .getWithQuery({
                            objectStore: objectStoreValue,
                            DocumentIds: ids === null ? [] : ids,
                            complete: completeValue ? 'true' : 'false',
                            searchResults: searchResultsValue
                                ? 'true'
                                : 'false',
                        })
                        .pipe(
                            switchMap(_ => this.displayByIds(DocumentIds)),
                            tap(_ => this.setLoading(false))
                        );
                }
            })
        );
    }

    getByID(
        objectStoreValue: string,
        id: string,
        complete = false,
        refresh = false
    ): Observable<Document> {
        return of(id).pipe(
            withLatestFrom(this.keys$, this.entities$),
            // tap(v => console.log('Get by ID', v)),
            switchMap(([idValue, keys, entities]) => {
                const strKeys = keys as string[];
                if (
                    strKeys.indexOf(idValue) >= 0 &&
                    !refresh &&
                    (!complete || entities.find(v => v.id === idValue).complete)
                ) {
                    return this.entities$.pipe(
                        map(v => {
                            const result = v.find(i => i.id === idValue);
                            if (
                                complete &&
                                !(result.complete || result.noAccess)
                            ) {
                                console.warn('Loaded by not complete');
                            }
                            return result;
                        })
                    );
                } else {
                    this.setLoading(true);
                    return super
                        .getWithQuery({
                            objectStore: objectStoreValue,
                            DocumentIds: idValue === null ? [] : [idValue],
                            complete: complete ? 'true' : 'false',
                        })
                        .pipe(
                            map(v => v[0]),
                            tap(_ => this.setLoading(false))
                        );
                }
            })
        );
    }
}
